import { css, cx, etiColors, mediaQueries } from '@eti/styles';
import { useCallback, useEffect, useState, useRef } from 'react';
import { vars } from '@etg/wings';
import { useDirection } from '@eti/providers';
import isFunction from 'lodash/isFunction';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const containerStyles = css`
  align-items: center;
  display: flex;
  height: 24px;
  justify-content: center;
  margin-bottom: 13px;
  padding-bottom: 16px;
  padding-left: 5%;
  padding-right: 5%;
  position: relative;

  @media ${mediaQueries.medium.up} {
    padding-left: 0;
    padding-right: 0;
  }
`;
const sliderStyles = css`
  position: relative;
  width: 100%;
`;
const trackStyles = css`
  background-color: #ccc;
  border-radius: 5px;
  height: 4px;
  position: absolute;
  width: 100%;
  z-index: 1;
`;
const rangeStyles = css`
  background-color: ${vars.colors.inputs.main};
  border-radius: 5px;
  height: 4px;
  position: absolute;
  z-index: 2;
`;
const sliderLeftValueStyles = isLtR => css`
  color: ${etiColors.grayScale[10]};
  font-size: 0.875rem;
  margin-top: 22px;
  position: absolute;
  ${isLtR ? 'left: 0' : 'right: -1px'}
`;
const sliderRightValueStyles = isLtR => css`
  color: ${etiColors.grayScale[10]};
  font-size: 0.875rem;
  margin-top: 22px;
  position: absolute;
  ${isLtR ? 'right: -1px' : 'left: 0'};
`;
const thumbStyles = css`
  /* stylelint-disable-next-line */
  -webkit-appearance: none;
  height: 0;
  outline: none;
  pointer-events: none;
  position: absolute;
  width: 100%;

  ::-webkit-slider-thumb {
    /* stylelint-disable-next-line */
    -webkit-appearance: none;
    appearance: none;
    background-color: ${etiColors.white};
    border: 1px solid ${etiColors.grayScale[7]};
    border-radius: 50%;
    cursor: pointer;
    height: 28px;
    margin-top: 2px;
    pointer-events: all;
    -webkit-tap-highlight-color: transparent;
    width: 28px;
  }

  &:focus::-webkit-slider-thumb {
    outline: 3px solid ${vars.colors.focusRing};
    outline-offset: 0.125rem;
  }

  ::-moz-range-thumb {
    background-color: ${etiColors.white};
    border: 1px solid ${etiColors.grayScale[7]};
    border-radius: 50%;
    cursor: pointer;
    height: 28px;
    margin-top: 2px;
    pointer-events: all;
    width: 28px;
  }

  &:focus::-moz-range-thumb {
    outline: 3px solid ${vars.colors.focusRing};
    outline-offset: 0.125rem;
  }
`;
const thumbRightStyles = css`
  z-index: 4;
`;
const thumbLeftStyles = shouldMinBeOverMax => css`
  z-index: ${shouldMinBeOverMax ? 5 : 3};
`;
const DoubleRangeSlider = _ref => {
  let {
    'data-testid': dataTestId,
    formatDisplayedValue,
    handleValuesChange,
    maxValue,
    minValue,
    handleValues = [minValue, maxValue],
    step = (maxValue - minValue) / 10,
    minValueLabel,
    maxValueLabel,
    getPriceCurrencySRText
  } = _ref;
  const {
    direction
  } = useDirection();
  const [maxState, setMaxState] = useState(maxValue);
  const [minState, setMinState] = useState(minValue);
  const range = useRef(null);
  const isLeftToRight = direction === 'ltr';
  const formatValue = value => isFunction(formatDisplayedValue) ? formatDisplayedValue(value) : value;

  // Convert to percentage
  const getPercent = useCallback(value => Math.round((value - minValue) / (maxValue - minValue) * 100), [minValue, maxValue]);

  // Set width of the range to decrease from the left side
  useEffect(() => {
    const minPercent = getPercent(minState);
    const maxPercent = getPercent(maxState);
    if (range.current && isLeftToRight) {
      range.current.style.left = `${minPercent}%`;
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
    if (range.current && !isLeftToRight) {
      range.current.style.right = `${minPercent}%`;
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [minState, getPercent, isLeftToRight, maxState]);

  // Set width of the range to decrease from the right side
  useEffect(() => {
    const minPercent = getPercent(minState);
    const maxPercent = getPercent(maxState);
    if (range.current) {
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [maxState, getPercent, minState]);

  // Used to reset filter
  useDeepCompareEffect(() => {
    setMaxState(handleValues[1]);
    setMinState(handleValues[0]);
  }, [handleValues]);
  const RangeFirstValue = /*#__PURE__*/_jsx("div", {
    "aria-hidden": true,
    className: sliderLeftValueStyles(isLeftToRight),
    children: formatValue(minState)
  });
  const RangeSecondValue = /*#__PURE__*/_jsx("div", {
    "aria-hidden": true,
    className: sliderRightValueStyles(isLeftToRight),
    children: formatValue(maxState)
  });
  const getPriceCurrencyAriaLabel = (value, valueLabel) => {
    return getPriceCurrencySRText ? `${getPriceCurrencySRText(formatValue(value))}${valueLabel ? `, ${valueLabel}` : ''}` : `${formatValue(value)}${valueLabel ? `, ${valueLabel}` : ''}`;
  };
  return /*#__PURE__*/_jsxs("div", {
    className: containerStyles,
    "data-testid": dataTestId,
    children: [/*#__PURE__*/_jsx("input", {
      "aria-label": getPriceCurrencyAriaLabel(minState, minValueLabel),
      "aria-valuenow": minState,
      "aria-valuetext": getPriceCurrencyAriaLabel(minState, minValueLabel),
      className: cx(thumbStyles, thumbLeftStyles(minState > maxValue - 100)),
      "data-testid": "handle-0",
      max: maxValue,
      min: minValue,
      onChange: event => {
        const value = Math.floor(Math.min(Number(event.target.value), maxState - 1));
        setMinState(value);
      },
      onKeyUp: event => {
        if (event.keyCode === 37 || event.keyCode === 39) {
          handleValuesChange([minState, maxState]);
        }
      },
      onMouseUp: () => {
        handleValuesChange([minState, maxState]);
      },
      onTouchEnd: () => {
        handleValuesChange([minState, maxState]);
      },
      step: step,
      type: "range",
      value: minState
    }), /*#__PURE__*/_jsx("input", {
      "aria-label": getPriceCurrencyAriaLabel(maxState, maxValueLabel),
      "aria-valuenow": maxState,
      "aria-valuetext": getPriceCurrencyAriaLabel(maxState, maxValueLabel),
      className: cx(thumbStyles, thumbRightStyles),
      max: maxValue,
      min: minValue,
      onChange: event => {
        const value = Math.ceil(Math.max(Number(event.target.value), minState + 1));
        setMaxState(value);
      },
      onKeyUp: event => {
        if (event.keyCode === 37 || event.keyCode === 39) {
          handleValuesChange([minState, maxState]);
        }
      },
      onMouseUp: () => {
        handleValuesChange([minState, maxState]);
      },
      onTouchEnd: () => {
        handleValuesChange([minState, maxState]);
      },
      step: step,
      type: "range",
      value: maxState
    }), /*#__PURE__*/_jsxs("div", {
      className: sliderStyles,
      children: [/*#__PURE__*/_jsx("div", {
        className: trackStyles
      }), /*#__PURE__*/_jsx("div", {
        className: rangeStyles,
        ref: range
      }), isLeftToRight ? RangeFirstValue : RangeSecondValue, isLeftToRight ? RangeSecondValue : RangeFirstValue]
    })]
  });
};
export default DoubleRangeSlider;
//# sourceMappingURL=DoubleRangeSlider.js.map